/* eslint-disable @typescript-eslint/no-explicit-any */
import createSagaMiddleware from 'redux-saga';
import { configureStore, Slice } from '@reduxjs/toolkit';
import { reducer } from './reducer';
import { appSagas } from './sagas';
import { ENVIRONMENT } from '../env';
import {
  subscribeToActionsMiddleware,
  subscribeToActions as _subscribeToActions,
  makeTestLogger,
  createSliceHook,
} from './helpers';
import { LocalStorage } from 'src/utils/storage';
import {
  createReducerInjector,
  createSagaInjector,
} from '@dynamika-shared/utils/saga';
import {
  createReduxLogger,
  createSagaLogger,
} from '@dynamika-shared/utils/reduxLoggers';
import { getUniqTabUUID } from './tools';
import { adminModules } from './roleModules/admin';

import { LOG_REDUX_ACTIONS } from '@dynamika-shared/utils/development';

export const subscribeToActions = _subscribeToActions;

const tabID = getUniqTabUUID();

const storeName = `Dynamika7--${tabID}-${
  location.hostname === 'localhost' ? 'localhost' : ENVIRONMENT
}`;

let hasDevTools = !!LocalStorage.getItem('reduxDevTools');

if (/localhost|dev/.test(window.location.hostname)) {
  hasDevTools = true;
}

const middlewares: any[] = [];

if (hasDevTools && LOG_REDUX_ACTIONS) {
  const logger = createReduxLogger('D7');

  middlewares.push(logger);
}

const saga = createSagaMiddleware(
  createSagaLogger(hasDevTools && LOG_REDUX_ACTIONS)
);

middlewares.push(saga);
middlewares.push(subscribeToActionsMiddleware);

export const store = configureStore({
  devTools: hasDevTools && {
    name: storeName,
  },
  reducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(middlewares),
});

declare global {
  interface Window {
    D7store: typeof store;
  }
}
// 1

if (/localhost/.test(window.location.hostname)) {
  let ctName = '';
  const ctx = sessionStorage.getItem('clinicalTrialContextStorage');

  try {
    const ctxObj = JSON.parse(ctx || '{}');

    ctName = ctxObj?.data?.configuration?.name ?? '';
  } catch {
    // do nothing
  }
  document.title = 'D7-' + tabID + (ctName ? `-${ctName}` : '');
  window.D7store = store;
}

/**
 * Injects a saga into the store
 * @param key The name of the saga
 * @param saga The saga function
 * @example
 * function mySaga() {
 *   yield takeLatest('MY_ACTION', function* () {}
 * }
 *
 * function MyComponent() {
 *   injectSaga('mySaga', mySaga);
 *   useEffect(() => {
 *     return () => cancelSaga('mySaga');
 *   }, []);
 *
 *   return <div>MyComponent</div>;
 * }
 */
export const {
  injectSaga,
  getInjectedSagas,
  injectSagas,
  cancelSaga,
  cancelSagas,
} = createSagaInjector(saga.run, appSagas);

/**
 * Injects a reducer into the store
 * @param key The name of the reducer
 * @param reducer The reducer function
 * @example
 * function myReducer(state, action) {
 *   switch(action.type) {
 *     case 'MY_ACTION':
 *       return { ...state, myAction: action.payload };
 *     default:
 *       return state;
 *   }
 * }
 *
 * function MyComponent() {
 *   injectReducer('myReducer', myReducer);
 *   useEffect(() => {
 *     return () => removeReducer('myReducer');
 *   }, []);
 *
 *   return <div>MyComponent</div>;
 * }
 */
export const {
  injectReducer,
  injectReducers,
  removeReducer,
  removeReducers,
  getInjectedReducers,
} = createReducerInjector(store.replaceReducer, reducer);

/**
 *
 * @param slice - The slice to inject
 * @example
 * const mySlice = createSlice({
 *   name: 'mySlice',
 *   initialState: { value: 0 },
 *   reducers: {
 *     increment: state => {
 *       state.value += 1;
 *     }
 *   }
 * });
 *
 * function MyComponent() {
 *   injectSlice(mySlice);
 *   useEffect(() => {
 *     return () => removeSlice(mySlice);
 *   }, []);
 *
 *   return <div>MyComponent</div>;
 * }
 */
export const injectSlice = <S extends Slice>(slice: S) => {
  injectReducer(slice.name, slice.reducer);

  return createSliceHook(slice.name as any, slice.actions);
};
export const injectSlices = <S extends Slice>(slices: S[]) => {
  const reducers = slices.reduce(
    (acc, slice) => {
      acc[slice.name] = slice.reducer;
      return acc;
    },
    {} as Record<string, any>
  );
  injectReducers(reducers);
};

export const removeSlice = <S extends Slice>(slice: S) => {
  removeReducer(slice.name);
};

export type RootState = ReturnType<typeof store.getState>;
export type RootStateWithInjection<T extends Slice[] = []> = RootState & {
  [K in T[number]['name']]: ReturnType<
    Extract<T[number], { name: K }>['getInitialState']
  >;
};
export type AppDispatch = typeof store.dispatch;
export type Store = typeof store;

export const makeTestStore = (enableLogs = false) => {
  const { showReduxLogs, logsMiddleware } = makeTestLogger(enableLogs);
  const testRoleReducers = adminModules.slices?.reduce(
    (acc, slice) => {
      acc[slice.name] = slice.reducer;
      return acc;
    },
    {} as Record<string, any>
  );

  const store = configureStore({
    reducer: {
      ...reducer,
      ...testRoleReducers,
    },
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: true,
      }).concat(subscribeToActionsMiddleware, logsMiddleware),
  });

  return {
    store,
    showReduxLogs,
  };
};
